import { I18nProvider } from '@lingui/react';
import { isAutomatedBrowser } from '@luminovo/commons';
import { analytics } from '@luminovo/data-stack';
import { createTheme } from '@luminovo/design-system';
import {
    AppRouterProvider,
    createAppRouter,
    subscribeToPageViews,
    subscribeToRouteTag,
} from '@luminovo/design-system/router-kit';
import { detectLanguageEnum } from '@luminovo/frontend-utils';
import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { type JSX } from 'react';

import { routeTree } from '@/routeTree.gen';
import { createAppQueryClient } from '@/shared/data/contexts/appQueryClient';
import { initializeI18n } from '@/shared/utils/internationalisation';

import { newNavigationOfPage } from './newNavigation';
import { createRouterAuth } from './routerAuth';
import { appRouterOptions, removeFailedQueriesOnNavigation } from './routerDefaults';
import { setSentryRouteTag } from './sentryRouteTag';
import { TenantAndAuthProviders } from './TenantAndAuthProviders';

// Auth0 parameters are passed as environment variables and are placed in window
// https://jakobzanker.de/blog/inject-environment-variables-into-a-react-app-docker-on-runtime/
declare global {
    interface Window {
        AUTH0_AUTHORITY: string;
        AUTH0_CLIENT_ID: string;
        MAGIC_BELL_API_KEY: string;
        SENTRY_DSN: string;
        SENTRY_TRACE_SAMPLE_RATE: string;
        SENTRY_RELEASE: string;
        SEGMENT_WRITE_KEY: string;
        INTERCOM_APP_ID: string;
        EPIBATOR_SHA: string;
        LUMINOVO_VERSION: string;
        /**
         * When set to "true", disables all third-party tracking (Segment, Sentry, HelpHero, etc.)
         * This is used for ITAR compliance to prevent any data leakage to third parties.
         */
        DISABLE_THIRD_PARTY_TRACKING: string;

        // this is added by a script in the index.html
        Intercom: any;
    }
}

const i18n = initializeI18n();
const theme = createTheme(detectLanguageEnum());

const queryClient = createAppQueryClient();
const auth = createRouterAuth();
// A URL no page claims gets `NotFoundPage`, the router's `defaultNotFoundComponent` (`appRouterOptions`).
const router = createAppRouter({
    routeTree,
    // Auth0's callback replaces the URL through `router.history`, the browser history the router creates for itself.
    history: 'browser',
    // Loaders and `beforeLoad` share the pages' cache; `_authenticated`'s waits on `auth`, then loads the session.
    context: { queryClient, auth },
    ...appRouterOptions,
});

// Both subscribe before the first render, so the router's first load counts.
// Tagged at once for the start URL, then as each load starts, so errors while the next page loads carry its name.
subscribeToRouteTag(router, setSentryRouteTag);
// A page counts once it has loaded and rendered, so a `beforeLoad` redirect sends only its target.
subscribeToPageViews(router, (routeName) => {
    // page prefix added because one of the routeId was deleted in Amplitude.
    analytics.page(`page: ${routeName}`, { new_navigation: newNavigationOfPage(router.state.matches) });
});
// A page the user comes back to fetches again what failed there.
removeFailedQueriesOnNavigation(router, queryClient);

// Type-only, so nothing here imports `@tanstack/react-router` at runtime.
declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

/** As a lazy chunk, so production never downloads the devtools. */
const AppDevtools = React.lazy(() => import('./AppDevtools').then((module) => ({ default: module.AppDevtools })));

const App: React.FunctionComponent = (): JSX.Element => {
    return (
        <React.StrictMode>
            <I18nProvider i18n={i18n}>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <QueryClientProvider client={queryClient}>
                            <TenantAndAuthProviders auth={auth} history={router.history}>
                                <AppRouterProvider router={router} />
                            </TenantAndAuthProviders>
                            {/* Not in automated browsers: the panels print route names, search params and query
                                state as text, which their text locators then match as well. */}
                            {import.meta.env.DEV && !isAutomatedBrowser() && (
                                <React.Suspense fallback={null}>
                                    <AppDevtools router={router} />
                                </React.Suspense>
                            )}
                        </QueryClientProvider>
                    </ThemeProvider>
                </StyledEngineProvider>
            </I18nProvider>
        </React.StrictMode>
    );
};

export default App;
